import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  Button,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FilterBar,
  FilterField,
  Input,
  Pagination,
  RowActions,
  SectionCard,
  Skeleton,
  StatusBadge,
  toast,
  type DataTableColumn,
  type RowActionItem,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import { RadioRow } from './RadioRow';
import { ShippingFeePolicyEditor } from './ShippingFeePolicyEditor';
import {
  useShippingFeePoliciesQuery,
  useShippingFeePolicyMutation,
  useShippingFeePolicyQuery,
} from '../hooks/useShippingFee';
import { SHIPPING_FEE_WRITE_ROLES } from '../lib/shippingFeeRbac';
import {
  type ShippingFeePolicyListParams,
  type ShippingFeePolicyPayload,
  type ShippingFeePolicySummary,
  type SupplierScope,
} from '../types/shippingFee';

const PAGE_SIZE = 20;

/** 목록 필터 공급사 구분(전체 포함) — 'ANY' = 미지정(전체). */
type ScopeFilter = SupplierScope | 'ANY';
const SCOPE_FILTER_OPTIONS: ReadonlyArray<ScopeFilter> = ['ANY', 'ALL', 'HQ', 'SUPPLIER'];

const formatDate = (iso: string, locale: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(locale);
};

/**
 * 국내 배송비조건 본문 — 통합 페이지(ShippingManagementPage)의 「국내 배송비」 탭과
 * 단독 페이지(ShippingFeePage) 양쪽에서 재사용된다. 로직/훅/API 는 무변경이며
 * PageShell 만 외부(컨테이너)가 제공한다. 등록 버튼은 본문 상단 우측 툴바로 렌더한다.
 */
export const ShippingFeeSection = () => {
  const { t, i18n } = useTranslation('shippingFee');
  const locale = i18n.language;
  const canWrite = useHasRole(SHIPPING_FEE_WRITE_ROLES);

  const scopeFilterLabels: Record<ScopeFilter, string> = {
    ANY: t('shippingFee.filterScopeAll'),
    ALL: t('shippingFee.supplierScope.ALL'),
    HQ: t('shippingFee.supplierScope.HQ'),
    SUPPLIER: t('shippingFee.supplierScope.SUPPLIER'),
  };

  /** 배송비설정 셀 표기 — FIXED=고정액, BY_*=상세보기, FREE=0원, VISIT=0원. */
  const feeSettingLabel = (row: ShippingFeePolicySummary): string => {
    if (row.shippingMethod === 'VISIT') return t('shippingFee.feeSetting.visit');
    switch (row.feeType) {
      case 'FIXED':
        return t('shippingFee.feeSetting.won', { amount: row.fixedFee.toLocaleString(locale) });
      case 'FREE':
        return t('shippingFee.feeSetting.free');
      default:
        return t('shippingFee.feeSetting.detail');
    }
  };

  // 필터 상태 (검색 버튼으로 확정 → applied 로 분리해 타이핑마다 재조회 방지).
  const [scopeDraft, setScopeDraft] = useState<ScopeFilter>('ANY');
  const [keywordDraft, setKeywordDraft] = useState('');
  const [applied, setApplied] = useState<{ scope: ScopeFilter; keyword: string }>({
    scope: 'ANY',
    keyword: '',
  });
  const [page, setPage] = useState(1);

  const listParams: ShippingFeePolicyListParams = useMemo(
    () => ({
      supplierScope: applied.scope === 'ANY' ? null : applied.scope,
      keyword: applied.keyword || null,
      page,
      size: PAGE_SIZE,
    }),
    [applied, page],
  );

  const query = useShippingFeePoliciesQuery(listParams);
  const { create, update, remove } = useShippingFeePolicyMutation();

  // 편집 대상 정책 id — 상세(tiers/지역가산)는 별도 GET 으로 가져온다.
  const [editPolicyId, setEditPolicyId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ShippingFeePolicySummary | null>(null);

  const detailQuery = useShippingFeePolicyQuery(editorOpen && !creating ? editPolicyId : null);

  const saving = create.isPending || update.isPending;
  const items = query.data?.items ?? [];
  const totalItems = query.data?.pagination.totalItems ?? 0;

  const runSearch = () => {
    setApplied({ scope: scopeDraft, keyword: keywordDraft.trim() });
    setPage(1);
  };

  const resetSearch = () => {
    setScopeDraft('ANY');
    setKeywordDraft('');
    setApplied({ scope: 'ANY', keyword: '' });
    setPage(1);
  };

  const openCreate = () => {
    setCreating(true);
    setEditPolicyId(null);
    setEditorOpen(true);
  };

  const openEdit = (row: ShippingFeePolicySummary) => {
    setCreating(false);
    setEditPolicyId(row.policyId);
    setEditorOpen(true);
  };

  const handleSubmit = (payload: ShippingFeePolicyPayload) => {
    if (!creating && editPolicyId) {
      update.mutate(
        { policyId: editPolicyId, payload },
        {
          onSuccess: () => {
            toast.success(t('shippingFee.toast.updated'));
            setEditorOpen(false);
          },
          onError: (err) => toast.error(friendlyMessage(err, t('shippingFee.toast.updateError'))),
        },
      );
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          toast.success(t('shippingFee.toast.created'));
          setEditorOpen(false);
        },
        onError: (err) => toast.error(friendlyMessage(err, t('shippingFee.toast.createError'))),
      });
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    remove.mutate(deleteTarget.policyId, {
      onSuccess: () => {
        toast.success(t('shippingFee.toast.deleted'));
        setDeleteTarget(null);
      },
      onError: (err) => toast.error(friendlyMessage(err, t('shippingFee.toast.deleteError'))),
    });
  };

  const columns: DataTableColumn<ShippingFeePolicySummary>[] = [
    {
      key: 'shippingFeeCode',
      header: t('shippingFee.columns.code'),
      width: 130,
      cell: (row) => (
        <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.shippingFeeCode}
        </span>
      ),
    },
    {
      key: 'name',
      header: t('shippingFee.columns.name'),
      cell: (row) => (
        <span className="flex items-center gap-2">
          <span className="text-[13px]" style={{ color: 'var(--content-strong)' }}>
            {row.name}
          </span>
          {/* M-8 ID81 기본 템플릿(1개만) — 신규 상품 자동선택 대상 표기. */}
          {row.isDefault ? (
            <StatusBadge label={t('shippingFee.badge.default')} variant="success" size="sm" />
          ) : null}
        </span>
      ),
    },
    {
      key: 'supplierScope',
      header: t('shippingFee.columns.scope'),
      width: 100,
      cell: (row) => (
        <span className="text-[13px]" style={{ color: 'var(--content-default)' }}>
          {t(`shippingFee.supplierScope.${row.supplierScope}`)}
          {row.supplierScope === 'SUPPLIER' && row.supplierKey ? ` (${row.supplierKey})` : ''}
        </span>
      ),
    },
    {
      key: 'shippingMethod',
      header: t('shippingFee.columns.method'),
      width: 90,
      cell: (row) => (
        <span className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
          {t(`shippingFee.shippingMethod.${row.shippingMethod}`)}
        </span>
      ),
    },
    {
      key: 'feeType',
      header: t('shippingFee.columns.feeType'),
      width: 90,
      cell: (row) => (
        <span className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
          {t(`shippingFee.feeType.${row.feeType}`)}
        </span>
      ),
    },
    {
      key: 'feeSetting',
      header: t('shippingFee.columns.feeSetting'),
      width: 110,
      align: 'right',
      cell: (row) => (
        <span className="text-[13px] tabular-nums" style={{ color: 'var(--content-default)' }}>
          {feeSettingLabel(row)}
        </span>
      ),
    },
    {
      key: 'paymentMethod',
      header: t('shippingFee.columns.paymentMethod'),
      width: 110,
      cell: (row) => (
        <span className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
          {t(`shippingFee.paymentMethod.${row.paymentMethod}`)}
        </span>
      ),
    },
    {
      key: 'regionSurcharge',
      header: t('shippingFee.columns.regionSurcharge'),
      width: 90,
      align: 'center',
      cell: (row) =>
        row.regionSurchargeEnabled ? (
          <StatusBadge label={t('shippingFee.badge.regionYes')} variant="info" size="sm" />
        ) : (
          <StatusBadge label={t('shippingFee.badge.regionNo')} variant="neutral" size="sm" />
        ),
    },
    {
      key: 'createdAt',
      header: t('shippingFee.columns.createdAt'),
      width: 100,
      cell: (row) => (
        <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {formatDate(row.createdAt, locale)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 56,
      align: 'right',
      cell: (row) => {
        if (!canWrite) return null;
        const actions: RowActionItem[] = [
          { key: 'edit', label: t('shippingFee.rowAction.edit'), onClick: () => openEdit(row) },
          {
            key: 'delete',
            label: t('shippingFee.rowAction.delete'),
            variant: 'danger' as const,
            separatorBefore: true,
            onClick: () => setDeleteTarget(row),
          },
        ];
        return <RowActions triggerLabel={t('shippingFee.rowAction.trigger')} actions={actions} />;
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4" data-testid="shipping-fee-section">
      {canWrite ? (
        <div className="flex justify-end">
          <Button size="sm" onClick={openCreate} data-testid="shipping-fee-add">
            <Plus size={15} aria-hidden="true" />
            {t('shippingFee.section.add')}
          </Button>
        </div>
      ) : null}

      <FilterBar
        filters={
          <>
            <div className="col-span-full">
              <RadioRow
                name="sfp-filter-scope"
                label={t('shippingFee.section.scopeLabel')}
                options={SCOPE_FILTER_OPTIONS}
                labels={scopeFilterLabels}
                value={scopeDraft}
                onChange={setScopeDraft}
                bordered={false}
              />
            </div>
            <FilterField label={t('shippingFee.section.keyword')} labelWidth={60}>
              <Input
                size="sm"
                value={keywordDraft}
                onChange={(e) => setKeywordDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runSearch();
                }}
                placeholder={t('shippingFee.section.keywordPlaceholder')}
                data-testid="shipping-fee-keyword"
              />
            </FilterField>
          </>
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={resetSearch}>
              {t('shippingFee.section.reset')}
            </Button>
            <Button size="sm" onClick={runSearch} data-testid="shipping-fee-search">
              {t('shippingFee.section.search')}
            </Button>
          </>
        }
      />

      <SectionCard title={t('shippingFee.section.listTitle')}>
        {query.isPending ? (
          <div role="status" aria-live="polite" className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-[var(--density-compact-row-h)] w-full" />
            ))}
          </div>
        ) : query.isError ? (
          <div
            role="alert"
            className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
            style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          >
            <p>{t('shippingFee.section.loadError')}</p>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              {t('shippingFee.section.retry')}
            </Button>
          </div>
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={items}
              rowKey={(row) => row.policyId}
              emptyState={<EmptyState size="inline" title={t('shippingFee.section.empty')} />}
              stickyHeader
              ariaLabel={t('shippingFee.section.ariaList')}
            />
            <Pagination
              total={totalItems}
              currentPage={page}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              className="mt-4"
            />
          </>
        )}
      </SectionCard>

      <ShippingFeePolicyEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        // 신규=null. 편집은 상세 GET 결과를 시드(로딩 중엔 다이얼로그가 빈 폼으로 열렸다
        // 데이터 도착 시 useEffect 로 리셋된다 — overseas 패턴과 동일).
        target={creating ? null : detailQuery.data ?? null}
        saving={saving}
        onSubmit={handleSubmit}
      />

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('shippingFee.deleteDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('shippingFee.deleteDialog.body', {
                name: deleteTarget ? `'${deleteTarget.name}'` : '',
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">
                {t('shippingFee.deleteDialog.cancel')}
              </Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={handleDelete}
            >
              {t('shippingFee.deleteDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
